type SoundEffect =
  | 'sparkle'
  | 'magic'
  | 'fanfare'
  | 'diceRoll'
  | 'hitSwing'
  | 'thunder'
  | 'thunderScene4'
  | 'zombieCroak'
  | 'zombieCroakScene4'
  | 'zombieLungeScene5'
  | 'pageRustleScene5'
  | 'diceRollInterim'
  | 'diceReveal'
  | 'diceRollD20'
  | 'diceRollD1'
  | 'diceRollSuccess'
  | 'diceRollFailure';

type SoundPlaybackOptions = {
  startTime?: number;
  duration?: number;
  volume?: number;
  playbackRate?: number;
};

interface CachedAudio {
  buffer: AudioBuffer;
  url: string;
}

import { isMobileBrowser } from '@/lib/client-utils';

const DICE_EFFECTS: ReadonlySet<SoundEffect> = new Set([
  'diceRoll',
  'diceRollInterim',
  'diceReveal',
  'diceRollD20',
  'diceRollD1',
  'diceRollSuccess',
  'diceRollFailure',
]);

const DICE_REVERB_TAIL_SECONDS = 0.55;
const DICE_REVERB_WET_GAIN = 0.2;
const DICE_REVERB_DEFAULT_SEND = 0.45;
const DICE_REVERB_INTERIM_SEND = 0.4;
const DICE_REVERB_RUMBLE_SEND = 0.12;

class AudioManagerClass {
  private audioContext: AudioContext | null = null;
  private audioBuffers: Map<SoundEffect, CachedAudio> = new Map();
  private isInitialized = false;
  private useFallback = false;
  private pendingSounds: Array<{ effect: SoundEffect; options?: SoundPlaybackOptions }> = [];
  private backgroundSuspended = false;

  private diceReverbConvolver: ConvolverNode | null = null;
  private diceReverbWet: GainNode | null = null;

  private rumbleSources: AudioBufferSourceNode[] = [];
  private rumbleGain: GainNode | null = null;
  private rumbleLowpass: BiquadFilterNode | null = null;

  private soundUrls: Record<SoundEffect, string> = {
    sparkle: '/game_ui/twinkle-appear-sfx.mp3',
    magic: '/game_ui/magic-spell.mp3',
    fanfare: '/game_ui/medieval-fanfare.mp3',
    diceRoll: '/game_ui/dice-roll-sfx.mp3',
    hitSwing: '/game_ui/hit-swing.mp3',
    thunder: '/new_ui/quest3/Quest3_Thunder.wav',
    thunderScene4: '/new_ui/quest3/Quest3_Scene4_Thunder.wav',
    zombieCroak: '/new_ui/quest3/Quest3_Scene2_ZombieCroak.wav',
    zombieCroakScene4: '/new_ui/quest3/Quest3_Scene4_ZombieCroak.wav',
    zombieLungeScene5: '/new_ui/quest3/Quest3_Scene5_ZombieLunge.wav',
    pageRustleScene5: '/new_ui/quest3/Q3S5_PageRustle.wav',
    diceRollInterim: '/dice-roll/dice-roll-sfx.m4a',
    diceReveal: '/dice-roll/dice-reveal.mp3',
    diceRollD20: '/dice-roll/dice-roll-d20.mp3',
    diceRollD1: '/dice-roll/dice-roll-d1.mp3',
    diceRollSuccess: '/dice-roll/dice-roll-success.mp3',
    diceRollFailure: '/dice-roll/dice-roll-failure.mp3',
  };

  async init(): Promise<void> {
    if (this.isInitialized) return;

    // Check if Web Audio API is supported
    if (!AudioManagerClass.isWebAudioSupported()) {
      console.warn('Web Audio API not supported, using fallback audio');
      this.useFallback = true;
      this.isInitialized = true;
      this.flushPendingSounds();
      return;
    }

    try {
      // Create AudioContext
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

      // Resume context if suspended (required for mobile)
      if (this.audioContext.state === 'suspended') {
        // Attempt resume but do not await to avoid hanging if browser blocks
        this.audioContext
          .resume()
          .then(() => {})
          .catch((err) => {
            console.warn('AudioContext resume failed:', err);
          });
      }

      // Preload all sound effects
      await this.preloadSounds();
      this.initDiceReverb();

      this.isInitialized = true;

      // Play any sounds that were queued before initialization
      this.flushPendingSounds();
    } catch (error) {
      console.warn('AudioManager initialization failed, falling back to HTML audio:', error);
      this.useFallback = true;
      this.audioContext = null;
      this.isInitialized = true;
      this.flushPendingSounds();
    }
  }

  private async preloadSounds(): Promise<void> {
    const loadPromises = Object.entries(this.soundUrls).map(async ([effect, url]) => {
      try {
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await this.audioContext!.decodeAudioData(arrayBuffer);

        this.audioBuffers.set(effect as SoundEffect, {
          buffer: audioBuffer,
          url,
        });
      } catch (error) {
        console.warn(`Failed to preload sound effect ${effect}:`, error);
      }
    });

    await Promise.all(loadPromises);
  }

  private initDiceReverb(): void {
    const ctx = this.audioContext;
    if (!ctx) return;

    const sampleRate = ctx.sampleRate;
    const length = Math.floor(sampleRate * DICE_REVERB_TAIL_SECONDS);
    const impulse = ctx.createBuffer(2, length, sampleRate);

    for (let ch = 0; ch < 2; ch++) {
      const data = impulse.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        const t = i / length;
        data[i] = (Math.random() * 2 - 1) * Math.exp(-t * 6.5);
      }
    }

    this.diceReverbConvolver = ctx.createConvolver();
    this.diceReverbConvolver.buffer = impulse;

    this.diceReverbWet = ctx.createGain();
    this.diceReverbWet.gain.value = DICE_REVERB_WET_GAIN;

    this.diceReverbConvolver.connect(this.diceReverbWet);
    this.diceReverbWet.connect(ctx.destination);
  }

  private getDiceReverbSend(effect: SoundEffect): number {
    if (effect === 'diceRollInterim') {
      return DICE_REVERB_INTERIM_SEND;
    }

    return DICE_REVERB_DEFAULT_SEND;
  }

  private flushPendingSounds(): void {
    while (this.pendingSounds.length > 0) {
      const sound = this.pendingSounds.shift();
      if (sound) {
        this.playSound(sound.effect, sound.options);
      }
    }
  }

  private playSound(effect: SoundEffect, options?: SoundPlaybackOptions): void {
    if (!this.isInitialized) {
      // Queue the sound to play after initialization
      this.pendingSounds.push({ effect, options });
      return;
    }

    // Use fallback if Web Audio API is not available or failed to initialize
    if (this.useFallback || !this.audioContext) {
      this.playFallback(effect, options);
      return;
    }

    const audioData = this.audioBuffers.get(effect);
    if (!audioData) {
      console.warn(`Sound effect ${effect} not found, trying fallback`);
      this.playFallback(effect);
      return;
    }

    try {
      const volume = options?.volume ?? 0.7;
      const playbackRate = options?.playbackRate ?? 1;
      const startTime = Math.max(options?.startTime ?? 0, 0);
      const requestedDuration = options?.duration;
      const availableDuration = Math.max(audioData.buffer.duration - startTime, 0);
      const duration =
        typeof requestedDuration === 'number'
          ? Math.min(Math.max(requestedDuration, 0), availableDuration)
          : undefined;

      const source = this.audioContext.createBufferSource();
      source.buffer = audioData.buffer;
      source.playbackRate.value = playbackRate;

      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = volume;

      source.connect(gainNode);

      if (DICE_EFFECTS.has(effect) && this.diceReverbConvolver) {
        const lowShelf = this.audioContext.createBiquadFilter();
        lowShelf.type = 'lowshelf';
        lowShelf.frequency.value = 300;
        lowShelf.gain.value = 3;

        const highShelf = this.audioContext.createBiquadFilter();
        highShelf.type = 'highshelf';
        highShelf.frequency.value = 4000;
        highShelf.gain.value = 2.5;

        const panner = this.audioContext.createStereoPanner();
        panner.pan.value = Math.random() * 0.6 - 0.3;

        gainNode.connect(lowShelf);
        lowShelf.connect(highShelf);
        highShelf.connect(panner);
        panner.connect(this.audioContext.destination);
        const reverbSend = this.audioContext.createGain();
        reverbSend.gain.value = this.getDiceReverbSend(effect);
        panner.connect(reverbSend);
        reverbSend.connect(this.diceReverbConvolver);
      } else {
        gainNode.connect(this.audioContext.destination);
      }

      if (duration !== undefined && duration > 0) {
        source.start(0, startTime, duration);
      } else {
        source.start(0, startTime);
      }
    } catch (error) {
      console.warn(
        `Failed to play sound effect ${effect} with Web Audio API, trying fallback:`,
        error,
      );
      this.playFallback(effect, options);
    }
  }

  play(effect: SoundEffect, options?: SoundPlaybackOptions): void {
    this.playSound(effect, options);
  }

  startDiceRumble(): void {
    if (!this.audioContext || this.useFallback) return;
    this.stopDiceRumble();

    const audioData = this.audioBuffers.get('diceRollInterim' as SoundEffect);
    if (!audioData) return;

    this.rumbleGain = this.audioContext.createGain();
    this.rumbleGain.gain.value = 0.18;

    this.rumbleLowpass = this.audioContext.createBiquadFilter();
    this.rumbleLowpass.type = 'lowpass';
    this.rumbleLowpass.frequency.value = 500;
    this.rumbleLowpass.Q.value = 0.7;

    this.rumbleGain.connect(this.rumbleLowpass);
    this.rumbleLowpass.connect(this.audioContext.destination);
    if (this.diceReverbConvolver) {
      const rumbleReverbSend = this.audioContext.createGain();
      rumbleReverbSend.gain.value = DICE_REVERB_RUMBLE_SEND;
      this.rumbleLowpass.connect(rumbleReverbSend);
      rumbleReverbSend.connect(this.diceReverbConvolver);
    }

    const segments = [
      { start: 1.0, end: 3.5, rate: 0.55, pan: -0.2 },
      { start: 4.5, end: 7.0, rate: 0.6, pan: 0.15 },
      { start: 8.0, end: 10.5, rate: 0.5, pan: -0.1 },
    ];

    for (const seg of segments) {
      const source = this.audioContext.createBufferSource();
      source.buffer = audioData.buffer;
      source.loop = true;
      source.loopStart = seg.start;
      source.loopEnd = seg.end;
      source.playbackRate.value = seg.rate;

      const panner = this.audioContext.createStereoPanner();
      panner.pan.value = seg.pan;

      source.connect(panner);
      panner.connect(this.rumbleGain!);
      source.start(0, seg.start);
      this.rumbleSources.push(source);
    }
  }

  stopDiceRumble(): void {
    if (this.rumbleGain && this.audioContext) {
      this.rumbleGain.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.3);
    }

    const sources = this.rumbleSources;
    const gain = this.rumbleGain;
    const lowpass = this.rumbleLowpass;
    this.rumbleSources = [];
    this.rumbleGain = null;
    this.rumbleLowpass = null;

    setTimeout(() => {
      sources.forEach((s) => {
        try {
          s.stop();
        } catch {
          /* already stopped */
        }
      });
      try {
        gain?.disconnect();
      } catch {
        /* already disconnected */
      }
      try {
        lowpass?.disconnect();
      } catch {
        /* already disconnected */
      }
    }, 350);
  }

  /**
   * Suspend or resume the Web Audio context when the app is backgrounded/foregrounded.
   * No-op if using HTMLAudio fallback or if the context is unavailable.
   */
  async setBackgroundMuted(shouldMute: boolean): Promise<void> {
    if (this.useFallback) return;
    if (!this.audioContext) return;

    try {
      if (shouldMute) {
        if (this.audioContext.state !== 'closed' && this.audioContext.state !== 'suspended') {
          await this.audioContext.suspend();
        }
        this.backgroundSuspended = true;
      } else {
        if (this.backgroundSuspended && this.audioContext.state !== 'running') {
          await this.audioContext.resume();
        }
        this.backgroundSuspended = false;
      }
    } catch (error) {
      console.warn('AudioContext background mute toggle failed:', error);
    }
  }

  // Fallback method for browsers that don't support Web Audio API
  private playFallback(effect: SoundEffect, options?: SoundPlaybackOptions): void {
    try {
      if (typeof document !== 'undefined' && document.hidden && isMobileBrowser()) {
        return;
      }
      const audio = new Audio(this.soundUrls[effect]);
      const volume = options?.volume ?? 0.7;
      const playbackRate = options?.playbackRate ?? 1;
      const startTime = Math.max(options?.startTime ?? 0, 0);
      const duration = options?.duration;

      audio.volume = volume;
      audio.playbackRate = playbackRate;

      const startPlayback = () => {
        if (startTime > 0) {
          audio.currentTime = startTime;
        }
        audio.play().catch((error) => {
          console.warn(`Fallback audio play failed for ${effect}:`, error);
        });
        if (typeof duration === 'number' && duration > 0) {
          window.setTimeout(() => {
            audio.pause();
            audio.currentTime = 0;
          }, duration * 1000);
        }
      };

      if (startTime > 0) {
        audio.addEventListener('loadedmetadata', startPlayback, { once: true });
        audio.load();
      } else {
        startPlayback();
      }
    } catch (error) {
      console.warn(`Fallback audio creation failed for ${effect}:`, error);
    }
  }

  // Check if Web Audio API is supported
  static isWebAudioSupported(): boolean {
    return !!(window.AudioContext || (window as any).webkitAudioContext);
  }
}

// Export singleton instance
export const AudioManager = new AudioManagerClass();

// Export types for use in other files
export type { SoundEffect, SoundPlaybackOptions };
